import {
  Stack,
  Typography,
  Card,
  CardContent,
  Box,
  Skeleton,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { GenericTable } from "@/components/GenericTable/GenericTable";

// Função para calcular a largura ideal do eixo Y baseada no tamanho dos labels
const calculateYAxisWidth = (data: { label: string; value: number }[]) => {
  if (!data || data.length === 0) return 150;
  const maxLabelLength = Math.max(...data.map((item) => item.label.length));
  const calculatedWidth = Math.max(150, maxLabelLength * 7 + 20);
  return Math.min(calculatedWidth, 400);
};
import SearchIcon from "@mui/icons-material/Search";
import { GetQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetQuestionnaries";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { oids } from "@/configs/Settings";
import { useAdministrativeFilters } from "@/pages/adminInsights/shared/useAdministrativeFilters";
import { useDateRangeFilter } from "@/components/common/DateRangeFilter/useDateRangeFilter";
import { endpoints } from "@/configs/Settings";
import { ViewHeader } from "@/components/schedule/components/ViewHeader";
import { DateFilterType } from "@/utils/interfaces";
import { useAxios } from "@/utils/useAxios";
import { useAuth } from "@/utils/useAuth";
import { HTTPResponseStatus } from "@/utils/enum";
import { NoData } from "@/components/common/NoData";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface PlanData {
  plan: string;
  status: string; // concluido, parcialmente, falhou, aberta
  practitioner: string;
  date: string;
  patientId: string;
  patientName?: string;
}

interface PatientPlanGroup {
  patientId: string;
  patientName: string;
  totalPlans: number;
  plans: PlanData[];
  statusCounts: {
    concluído: number;
    aberta: number;
    parcial: number;
    falhou: number;
  };
}

interface PlanStats {
  status: string;
  count: number;
  percentage: number;
}

export function TreatmentPlansAnalysis() {
  const theme = useTheme();
  const { organizationId } = useOrganizationContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [patientDialogOpen, setPatientDialogOpen] = useState<boolean>(false);
  const [patientPlans, setPatientPlans] = useState<PlanData[]>([]);
  const [patients, setPatients] = useState<Map<string, fhir5.Patient>>(
    new Map()
  );
  const [dialogSearch, setDialogSearch] = useState<string>("");

  const {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  } = useDateRangeFilter({ defaultFilterType: "month" });

  // Converte dateFilterType para DateFilterType (removendo "year" se necessário)
  const filterType: DateFilterType =
    dateFilterType === "year" ? "month" : (dateFilterType as DateFilterType);

  // Wrapper para handleFilterTypeChange que converte "year" para "month"
  const handleFilterTypeChangeWrapper = (type: DateFilterType) => {
    handleFilterTypeChange(type as any);
  };

  const {
    selectedLocation,
    selectedPractitioner,
    handleLocationChange,
    handlePractitionerChange,
    location,
    locationLoading,
    practitionerRole,
    practitioner,
    practitionerRoleLoading,
  } = useAdministrativeFilters({
    onChange: (filters) => {
      console.log("Filtros de planos atualizados:", filters);
    },
  });

  const author = `${oids.cnpj}-${organizationId}`;
  const practitionerFilter = selectedPractitioner || "all";

  const handleTabChange = (_: any, newValue: number) => setCurrentTab(newValue);

  // Função para resetar todos os filtros
  const handleReset = () => {
    handleLocationChange("");
    handlePractitionerChange("");
  };

  // Função para obter o nome do profissional a partir da referência
  const getPractitionerName = useCallback(
    (practitionerReference: string) => {
      if (!practitionerReference) return "Não informado";

      // Extrai o ID do profissional da referência
      const practId = practitionerReference.split("/")[1];

      // Busca o recurso Practitioner correspondente
      const pract = practitioner?.find((p: any) => p.resource?.id === practId);

      // Retorna o nome formatado
      const displayName =
        pract?.resource?.name?.[0]?.text ||
        `${pract?.resource?.name?.[0]?.given?.join(" ") || ""} ${
          pract?.resource?.name?.[0]?.family || ""
        }`.trim();

      return displayName || practId;
    },
    [practitioner]
  );

  // Função para buscar dados dos pacientes
  const fetchPatients = async (patientIds: string[]) => {
    try {
      const patientMap = new Map<string, fhir5.Patient>();

      for (const patientRef of patientIds) {
        const patientId = patientRef.split("/")[1];
        if (!patientId || patientMap.has(patientId)) continue;

        const url = `${endpoints.MPIFHIR}/Patient/${patientId}`;
        const headers = {
          Authorization: await useAuth.getUserToken(),
          "Content-Type": "application/fhir+json",
          Accept: "application/fhir+json",
        };

        try {
          const response = await useAxios.get(url, { headers, timeout: 20000 });
          if (response.status === HTTPResponseStatus.Success && response.data) {
            patientMap.set(patientId, response.data);
          }
        } catch (err) {
          console.error(`Erro ao buscar paciente ${patientId}:`, err);
        }
      }

      setPatients(patientMap);
    } catch (error) {
      console.error("Erro ao buscar pacientes:", error);
    }
  };

  const fetchPlans = useCallback(async () => {
    if (!author) return;
    setLoading(true);
    try {
      const response = await GetQuestionnaireResponse({
        author,
        practitioner: practitionerFilter,
        hasProtocols: false,
        questionnaire: "Questionnaire/clinicalRegister",
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });

      const entries: any[] = response?.entry || [];
      type Raw = {
        plan: string;
        status: string;
        practitioner: string;
        date: string;
        patientId: string;
        authored: Date;
      };
      const raw: Raw[] = [];

      for (const entry of entries) {
        const rsrc = entry?.resource;
        if (!rsrc || rsrc.resourceType !== "QuestionnaireResponse") continue;

        const patientId = rsrc.subject?.reference || "unknown";
        const authoredDate = rsrc.authored
          ? new Date(rsrc.authored)
          : new Date(0);
        const date = authoredDate.toLocaleDateString();

        const plansItem = rsrc.item?.find(
          (it: any) => it.linkId === "4e1679c2-3e1d-444d-d438-28bdbeb1e0ee"
        );
        if (!plansItem?.item) continue;

        for (const planGroup of plansItem.item) {
          if (
            !planGroup.item ||
            planGroup.linkId !== "4e1679c2-3e1d-444d-d438-28bdbeb1e0ee"
          )
            continue;

          const planItem = planGroup.item.find(
            (it: any) => it.linkId === "477c970e-a76b-4fe9-89d4-65cd19481b7b"
          );
          const planText = planItem?.answer?.[0]?.valueString;
          if (!planText) continue;

          const statusItem = planGroup.item.find(
            (it: any) => it.linkId === "ba000da7-3e8e-4b03-faf2-4089aeb2852d"
          );
          let status = "aberta";
          if (statusItem?.answer?.[0]?.valueCoding) {
            const code = statusItem.answer[0].valueCoding.code;
            const display = statusItem.answer[0].valueCoding.display;
            if (code === "concluded" || display === "Concluído")
              status = "concluído";
            else if (code === "partially" || display === "Parcialmente")
              status = "parcial";
            else if (code === "failed" || display === "Falhou")
              status = "falhou";
          }

          // Nome do profissional
          const practitionerName = getPractitionerName(
            rsrc.author?.reference || ""
          );

          raw.push({
            plan: planText,
            status,
            practitioner: practitionerName,
            date,
            patientId,
            authored: authoredDate,
          });
        }
      }

      // Paridade: agrupar por paciente + texto do plano (não por status)
      // Para cada grupo (paciente + plano), aplicar paridade
      // - PAR: mostra apenas 1 registro (com status ou mais recente)
      // - ÍMPAR: mostra até 2 registros (um com status + um sem status)
      // - EXCEÇÃO: Sempre manter itens do QuestionnaireResponse mais recente do paciente
      const patientLatestAuthored = new Map<string, number>();
      for (const v of raw) {
        const t = v.authored.getTime();
        const prev = patientLatestAuthored.get(v.patientId) || 0;
        if (t > prev) patientLatestAuthored.set(v.patientId, t);
      }

      // Agrupar por paciente + plano (não por status)
      const groups = new Map<string, Raw[]>();
      for (const it of raw) {
        const key = `${it.patientId}|${it.plan}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(it);
      }

      const parity: PlanData[] = [];
      for (const [, arr] of groups.entries()) {
        if (arr.length === 1) {
          parity.push({
            plan: arr[0].plan,
            status: arr[0].status,
            practitioner: arr[0].practitioner,
            date: arr[0].date,
            patientId: arr[0].patientId,
          });
          continue;
        }

        // Ordena por data (mais recente primeiro)
        const sorted = arr.sort(
          (a, b) => b.authored.getTime() - a.authored.getTime()
        );
        const isPair = arr.length % 2 === 0;
        const withStatus = sorted.filter(
          (x) => x.status && x.status !== "aberta"
        );
        const withoutStatus = sorted.filter(
          (x) => !x.status || x.status === "aberta"
        );

        const latestAuthoredTs = patientLatestAuthored.get(sorted[0].patientId);
        const latestBelongsToCurrentQR =
          latestAuthoredTs !== undefined &&
          sorted[0].authored.getTime() === latestAuthoredTs;

        // Se pertence ao QR mais recente, sempre manter TODAS as entradas
        if (latestBelongsToCurrentQR) {
          // Para QR mais recente, sempre manter todas as entradas (ignorar paridade)
          for (const item of sorted) {
            parity.push({
              plan: item.plan,
              status: item.status,
              practitioner: item.practitioner,
              date: item.date,
              patientId: item.patientId,
            });
          }
        } else {
          // Lógica normal de paridade
          if (isPair) {
            // PAR: apenas 1 registro
            if (withStatus.length > 0) {
              parity.push({
                plan: withStatus[0].plan,
                status: withStatus[0].status,
                practitioner: withStatus[0].practitioner,
                date: withStatus[0].date,
                patientId: withStatus[0].patientId,
              });
            } else {
              parity.push({
                plan: sorted[0].plan,
                status: sorted[0].status,
                practitioner: sorted[0].practitioner,
                date: sorted[0].date,
                patientId: sorted[0].patientId,
              });
            }
          } else {
            // ÍMPAR: até 2 registros
            if (withStatus.length > 0) {
              parity.push({
                plan: withStatus[0].plan,
                status: withStatus[0].status,
                practitioner: withStatus[0].practitioner,
                date: withStatus[0].date,
                patientId: withStatus[0].patientId,
              });
              if (withoutStatus.length > 0) {
                parity.push({
                  plan: withoutStatus[0].plan,
                  status: withoutStatus[0].status,
                  practitioner: withoutStatus[0].practitioner,
                  date: withoutStatus[0].date,
                  patientId: withoutStatus[0].patientId,
                });
              }
            } else {
              parity.push({
                plan: sorted[0].plan,
                status: sorted[0].status,
                practitioner: sorted[0].practitioner,
                date: sorted[0].date,
                patientId: sorted[0].patientId,
              });
            }
          }
        }
      }

      setPlans(parity);

      // Buscar dados dos pacientes
      const uniquePatientIds = Array.from(
        new Set(parity.map((p) => p.patientId))
      );
      await fetchPatients(uniquePatientIds);
    } catch (error) {
      console.error("Erro ao buscar planos:", error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [
    author,
    practitionerFilter,
    dateRange?.startDate,
    dateRange?.endDate,
    getPractitionerName,
  ]);

  useEffect(() => {
    if (author) fetchPlans();
  }, [fetchPlans, author]);

  // Agrupar planos por paciente
  const patientGroups = useMemo(() => {
    const groups = new Map<string, PatientPlanGroup>();

    for (const plan of plans) {
      const patientId = plan.patientId.split("/")[1];
      const patient = patients.get(patientId);
      const patientName =
        patient?.name?.[0]?.text ||
        `${patient?.name?.[0]?.given?.join(" ") || ""} ${
          patient?.name?.[0]?.family || ""
        }`.trim() ||
        "Não informado";

      if (!groups.has(patientId)) {
        groups.set(patientId, {
          patientId,
          patientName,
          totalPlans: 0,
          plans: [],
          statusCounts: {
            concluído: 0,
            aberta: 0,
            parcial: 0,
            falhou: 0,
          },
        });
      }

      const group = groups.get(patientId)!;
      group.totalPlans++;
      group.plans.push({ ...plan, patientName });

      // Contar status
      const status = plan.status || "aberta";
      if (status in group.statusCounts) {
        group.statusCounts[status as keyof typeof group.statusCounts]++;
      }
    }

    return Array.from(groups.values()).sort(
      (a, b) => b.totalPlans - a.totalPlans
    );
  }, [plans, patients]);

  // Processar estatísticas de planos
  const { planStats, totalPlans } = useMemo(() => {
    const statusMap = new Map<string, number>();

    for (const plan of plans) {
      const status = plan.status || "aberta";
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    }

    const total = plans.length;
    const stats: PlanStats[] = Array.from(statusMap.entries())
      .map(([status, count]) => ({
        status,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      planStats: stats,
      totalPlans: total,
    };
  }, [plans]);

  // Mapa de tradução de status
  const statusTranslation: Record<string, string> = {
    concluído: "Concluído",
    aberta: "Em Aberto",
    parcial: "Parcialmente",
    falhou: "Falhou",
  };

  // Dados para o gráfico de pizza (agora barra)
  const chartData = useMemo(() => {
    return planStats.map((stat, idx) => ({
      id: idx,
      value: stat.count,
      percentage: stat.percentage,
      label:
        statusTranslation[stat.status] ||
        stat.status.charAt(0).toUpperCase() + stat.status.slice(1),
    }));
  }, [planStats]);

  // Função para lidar com o clique na linha da tabela
  const handleRowClick = (row: any) => {
    const group = patientGroups.find((g) => g.patientName === row.patientName);
    if (group) {
      setSelectedPatient(group);
      setPatientPlans(group.plans);
      setDialogSearch(""); // Resetar busca ao abrir o diálogo
      setPatientDialogOpen(true);
    }
  };

  // Filtrar grupos de pacientes baseado na busca
  const filteredPatientGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? patientGroups.filter(
          (group) =>
            group.patientName?.toLowerCase().includes(q) ||
            group.plans.some(
              (p) =>
                p.plan?.toLowerCase().includes(q) ||
                p.status?.toLowerCase().includes(q)
            )
        )
      : patientGroups;

    return filtered.map((group) => ({
      patientName: group.patientName,
      totalPlans: group.totalPlans,
      concluído: group.statusCounts.concluído,
      aberta: group.statusCounts.aberta,
      parcial: group.statusCounts.parcial,
      falhou: group.statusCounts.falhou,
    }));
  }, [patientGroups, search]);

  // Filtrar planos do diálogo baseado na busca
  const filteredDialogPlans = useMemo(() => {
    const q = dialogSearch.trim().toLowerCase();
    return q
      ? patientPlans.filter(
          (plan) =>
            plan.plan?.toLowerCase().includes(q) ||
            plan.status?.toLowerCase().includes(q) ||
            plan.practitioner?.toLowerCase().includes(q)
        )
      : patientPlans;
  }, [patientPlans, dialogSearch]);

  // Renderizar skeleton durante carregamento
  if (loading) {
    return (
      <Stack spacing={3} width="100%">
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="rectangular" height={250} />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack width="100%" spacing={3}>
      {/* Barra de Filtros */}
      <ViewHeader
        title="Planos de Tratamento"
        dateFilter={selectedDate}
        filterType={filterType}
        setDateFilter={handleDateChange}
        setFilterType={handleFilterTypeChangeWrapper}
        showAdminFilters={true}
        adminFiltersProps={{
          selectedLocation,
          selectedPractitioner,
          onLocationChange: handleLocationChange,
          onPractitionerChange: handlePractitionerChange,
          location,
          locationLoading,
          practitionerRole,
          practitioner,
          practitionerRoleLoading,
          onResetFilters: () => {
            handleLocationChange("");
            handlePractitionerChange("");
          },
          setIsSearchActive: () => {},
        }}
        onReset={handleReset}
        resetButtonProps={{
          tooltip: "Limpar todos os filtros",
        }}
      />

      {totalPlans > 0 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab label="Status dos Planos" />
              <Tab label="Detalhamento" />
            </Tabs>
          </Box>

          <TabPanel value={currentTab} index={0}>
            <Card>
              <CardContent>
                <Stack spacing={5}>
                  {/* Gráfico de Status dos Planos */}
                  {chartData.length > 0 ? (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        {(() => {
                          const sorted = [...chartData].sort(
                            (a, b) => b.value - a.value
                          );
                          const total = chartData.reduce(
                            (acc, item) => acc + item.value,
                            0
                          );
                          const percentage =
                            total > 0
                              ? Math.round((sorted[0].value / total) * 100)
                              : 0;
                          return `${percentage}% dos planos de tratamento estão como '${sorted[0].label}'`;
                        })()}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Acompanhe o sucesso dos tratamentos propostos. Um número
                        alto de planos que "Falharam" pode indicar a necessidade
                        de rever as abordagens terapêuticas.
                      </Typography>
                      <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={chartData}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis
                              dataKey="label"
                              type="category"
                              width={calculateYAxisWidth(chartData)}
                              tick={{ fontSize: 12 }}
                            />
                            <RechartsTooltip
                              formatter={(value: number | undefined) => {
                                if (value === undefined)
                                  return ["0", "Quantidade"];
                                const total = chartData.reduce(
                                  (acc, item) => acc + item.value,
                                  0
                                );
                                const percentage =
                                  total > 0
                                    ? Math.round((value / total) * 100)
                                    : 0;
                                return [
                                  `${value} (${percentage}%)`,
                                  "Quantidade",
                                ];
                              }}
                            />
                            <Bar dataKey="value" name="Quantidade">
                              {chartData.map((entry, index) => {
                                const maxValue = Math.max(
                                  ...chartData.map((d) => d.value)
                                );
                                const isHighlight = entry.value === maxValue;
                                return (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      isHighlight
                                        ? theme.palette.primary.main
                                        : "#9E9E9E"
                                    }
                                  />
                                );
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  ) : (
                    <NoData message="Sem dados de status de planos." />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <Card>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder="Buscar por paciente ou plano"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <GenericTable
                  tableColumns={[
                    {
                      column: "Paciente",
                      rowName: "patientName",
                      align: "left",
                    },
                    {
                      column: "Concluído",
                      rowName: "concluído",
                      width: "100px",
                    },
                    { column: "Aberta", rowName: "aberta", width: "80px" },
                    { column: "Parcial", rowName: "parcial", width: "80px" },
                    { column: "Falhou", rowName: "falhou", width: "80px" },
                    { column: "Total", rowName: "totalPlans", width: "80px" },
                  ]}
                  tableRows={filteredPatientGroups}
                  characterLimit={60}
                  onRowClick={handleRowClick}
                />
              </CardContent>
            </Card>
          </TabPanel>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Nenhum plano encontrado
          </Typography>
        </Box>
      )}

      {/* Dialog para exibir planos do paciente */}
      {patientDialogOpen && selectedPatient && (
        <Dialog
          open={patientDialogOpen}
          onClose={() => setPatientDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Planos de Tratamento de: {selectedPatient.patientName}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                placeholder="Buscar por plano, status ou profissional"
                value={dialogSearch}
                onChange={(e) => setDialogSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {filteredDialogPlans.length > 0 ? (
              <GenericTable
                tableColumns={[
                  { column: "Plano", rowName: "plan", align: "left" },
                  { column: "Status", rowName: "statusLabel", width: "150px" },
                  { column: "Profissional", rowName: "practitioner" },
                  { column: "Data", rowName: "date", width: "120px" },
                ]}
                tableRows={filteredDialogPlans.map((plan) => ({
                  plan: plan.plan,
                  statusLabel: plan.status
                    ? plan.status.charAt(0).toUpperCase() + plan.status.slice(1)
                    : "Não informado",
                  practitioner: plan.practitioner,
                  date: plan.date,
                }))}
                characterLimit={80}
              />
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 2 }}>
                {dialogSearch
                  ? "Nenhum plano encontrado com os critérios de busca."
                  : "Nenhum plano encontrado para este paciente."}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPatientDialogOpen(false);
                setSelectedPatient(null);
                setPatientPlans([]);
                setDialogSearch("");
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}

export default TreatmentPlansAnalysis;
